import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { PanelRight, Heart, Swords } from "lucide-react";

interface V2HUDTriggerProps {
  onClick: () => void;
  turn?: number;
  hp?: number;
  maxHp?: number;
  inCombat?: boolean;
  /** On mobile, push the trigger higher to avoid the input area. */
  isMobile?: boolean;
}

/**
 * Floating icon button (bottom-right) that opens the V2 GameHUD slide-out.
 * Shows a compact status badge (turn + HP) and pulses during combat.
 */
export function V2HUDTrigger({ onClick, turn, hp, maxHp, inCombat, isMobile }: V2HUDTriggerProps) {
  const showTurn = turn != null;
  const showHp = hp != null && maxHp != null;
  const hpPercent = showHp ? Math.min(1, Math.max(0, hp! / maxHp!)) : 1;

  return (
    <div className={cn(
      "fixed right-6 z-30 flex flex-col items-end gap-1.5",
      isMobile ? "bottom-20" : "bottom-6",
    )}>
      {/* Status badge */}
      {(showTurn || showHp) && (
        <div className="flex items-center gap-1.5 bg-card/90 border border-border/60 rounded-full px-2.5 py-1 shadow-lg backdrop-blur-sm">
          {showTurn && (
            <span className="text-[10px] font-mono tabular-nums text-foreground/70">
              T{turn}
            </span>
          )}
          {showHp && (
            <div className="flex items-center gap-1">
              <Heart
                className={cn(
                  "w-3 h-3",
                  hpPercent < 0.25 ? "text-red-500" : hpPercent < 0.5 ? "text-amber-500" : "text-green-500",
                )}
                fill="currentColor"
              />
              <span className="text-[10px] font-mono tabular-nums text-foreground/70">
                {hp}/{maxHp}
              </span>
            </div>
          )}
        </div>
      )}

      {/* Trigger button */}
      <Button
        variant="outline"
        size="icon"
        className={cn(
          "h-10 w-10 rounded-full shadow-lg border-border/60 bg-card/90 backdrop-blur-sm hover:bg-card transition-all",
          inCombat && "animate-pulse border-red-500/40",
        )}
        onClick={onClick}
        aria-label="Open game HUD"
      >
        {inCombat ? (
          <Swords className="w-4.5 h-4.5 text-red-500" />
        ) : (
          <PanelRight className="w-4.5 h-4.5 text-foreground/70" />
        )}
      </Button>
    </div>
  );
}
